import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { email: "", password: "" } });

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const result = await login(data.email, data.password);
      if (result.requires_2fa) {
        navigate(`/login/verify-2fa?email=${encodeURIComponent(result.email)}`);
      } else {
        toast.success("Welcome back!");
        navigate("/home");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      {/* Left brand panel */}
      <div
        style={{
          flex: "0 0 55%",
          backgroundColor: "#C41E1E",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem",
          position: "relative",
        }}
        className="hidden md:flex"
      >
        <h1
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontWeight: 700,
            fontSize: "3rem",
            color: "#FFFFFF",
            letterSpacing: "6px",
            marginBottom: "1rem",
          }}
        >
          ELEMENTAR
        </h1>

        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(255,255,255,0.4)", marginBottom: "1.5rem" }} />

        <p
          style={{
            fontFamily: "'Cormorant Garamond', Garamond, serif",
            fontWeight: 400,
            fontSize: "1.15rem",
            color: "rgba(255,255,255,0.85)",
            letterSpacing: "3px",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          Unified alternative investment intelligence
        </p>

        {/* Bottom attribution */}
        <p
          style={{
            position: "absolute",
            bottom: "2rem",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.7rem",
            letterSpacing: "3px",
            color: "rgba(255,255,255,0.4)",
            textTransform: "uppercase",
          }}
        >
          From Wooden Owl AG
        </p>
      </div>

      {/* Right form panel */}
      <div
        style={{
          flex: 1,
          backgroundColor: "#0A0A0A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <div style={{ width: "100%", maxWidth: "380px" }}>
          {/* Mobile logo — visible only on small screens */}
          <div className="md:hidden" style={{ textAlign: "center", marginBottom: "2rem" }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "1.8rem",
                color: "#C41E1E",
                letterSpacing: "4px",
              }}
            >
              ELEMENTAR
            </h2>
          </div>

          <h2
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 400,
              fontSize: "1.75rem",
              color: "#F0EDE8",
              marginBottom: "0.5rem",
            }}
          >
            Welcome back
          </h2>
          <p
            style={{
              fontFamily: "'Cormorant Garamond', Garamond, serif",
              fontSize: "1rem",
              color: "#8A8A8A",
              marginBottom: "2.5rem",
            }}
          >
            Log in to your Elementar dashboard
          </p>

          <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div>
              <label
                htmlFor="email"
                style={{
                  display: "block",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "#8A8A8A",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  marginBottom: "0.5rem",
                }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@office.com"
                autoComplete="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email",
                  },
                })}
                style={{
                  width: "100%",
                  padding: "0.85rem 1rem",
                  backgroundColor: "#141414",
                  border: "1px solid #222",
                  borderRadius: "6px",
                  color: "#F0EDE8",
                  fontFamily: "'Cormorant Garamond', Garamond, serif",
                  fontSize: "1rem",
                  outline: "none",
                  transition: "border-color 0.3s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#C41E1E")}
                onBlur={(e) => (e.target.style.borderColor = "#222")}
              />
              {errors.email && (
                <p style={{ marginTop: "0.35rem", fontSize: "0.8rem", color: "#E8593C" }}>{errors.email.message}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                style={{
                  display: "block",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "#8A8A8A",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  marginBottom: "0.5rem",
                }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                {...register("password", {
                  required: "Password is required",
                })}
                style={{
                  width: "100%",
                  padding: "0.85rem 1rem",
                  backgroundColor: "#141414",
                  border: "1px solid #222",
                  borderRadius: "6px",
                  color: "#F0EDE8",
                  fontFamily: "'Cormorant Garamond', Garamond, serif",
                  fontSize: "1rem",
                  outline: "none",
                  transition: "border-color 0.3s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#C41E1E")}
                onBlur={(e) => (e.target.style.borderColor = "#222")}
              />
              {errors.password && (
                <p style={{ marginTop: "0.35rem", fontSize: "0.8rem", color: "#E8593C" }}>{errors.password.message}</p>
              )}
              <div style={{ textAlign: "right", marginTop: "0.5rem" }}>
                <a
                  href="/forgot-password"
                  style={{ fontSize: "0.8rem", color: "#8A8A8A", textDecoration: "none", borderBottom: "1px solid transparent" }}
                  onMouseEnter={(e) => { e.target.style.color = "#C41E1E"; e.target.style.borderBottomColor = "rgba(196,30,30,0.3)"; }}
                  onMouseLeave={(e) => { e.target.style.color = "#8A8A8A"; e.target.style.borderBottomColor = "transparent"; }}
                >
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "0.85rem",
                backgroundColor: "#C41E1E",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "6px",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.8rem",
                fontWeight: 500,
                letterSpacing: "2px",
                textTransform: "uppercase",
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
                transition: "background-color 0.3s, opacity 0.3s",
                marginTop: "0.5rem",
              }}
              onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#8B1515"; }}
              onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#C41E1E"; }}
            >
              {submitting ? "Signing in..." : "Log in"}
            </button>
          </form>

          <p style={{ marginTop: "2rem", textAlign: "center", fontSize: "0.85rem", color: "#8A8A8A" }}>
            Need access?{" "}
            <a href="mailto:eilon@elementarai.com" style={{ color: "#C41E1E", textDecoration: "none", borderBottom: "1px solid rgba(196,30,30,0.3)" }}>
              Contact us
            </a>
          </p>

          <p style={{ marginTop: "0.75rem", textAlign: "center", fontSize: "0.85rem", color: "#8A8A8A" }}>
            New to Elementar?{" "}
            <Link to="/register" style={{ color: "#C41E1E", textDecoration: "none", borderBottom: "1px solid rgba(196,30,30,0.3)" }}>
              Create an account
            </Link>
          </p>

          {/* Swiss badge */}
          <div style={{ marginTop: "3rem", textAlign: "center" }}>
            <p style={{ fontSize: "0.7rem", color: "#555", letterSpacing: "1px" }}>
              🇨🇭 Swiss-domiciled
            </p>
            <p style={{ fontSize: "0.65rem", color: "#444", marginTop: "0.35rem" }}>
              © 2026 Elementar
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
